// React and Router
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

// Firebase
import { auth } from "../../firebase.js";
import { onAuthStateChanged } from "firebase/auth";

// Components
import Header from "../shared/Header";

// Styles
import "../../css/StorageManagement.css";

const API_BASE = "https://bluebirddocumentationadmin.pythonanywhere.com";

function StorageManagement() {
    const { uid } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [storageData, setStorageData] = useState(null);
    const [bonuses, setBonuses] = useState([]);
    const [billingMode, setBillingMode] = useState("hard_cap");
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [currentUserUid, setCurrentUserUid] = useState(null);
    const [upgradeSuccess, setUpgradeSuccess] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setIsLoggedIn(true);
                setCurrentUserUid(user.uid);
            } else {
                setIsLoggedIn(false);
                navigate("/login");
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const fetchStorageDetails = useCallback(async () => {
        if (!currentUserUid) return;
        setLoading(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${API_BASE}/users/${uid}/storage/details/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`,
                    "auth-token": `Bearer ${token}`,
                    "requesting-user-uid": currentUserUid,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setStorageData(data);
                setBillingMode(data.storage_info?.billing_mode || "hard_cap");
            }
        } catch (error) {
            console.error("Error fetching storage details:", error);
        } finally {
            setLoading(false);
        }
    }, [uid, currentUserUid]);

    const fetchBonuses = useCallback(async () => {
        if (!currentUserUid) return;
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${API_BASE}/users/${uid}/storage/bonuses/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`,
                    "auth-token": `Bearer ${token}`,
                    "requesting-user-uid": currentUserUid,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setBonuses(data.bonuses || []);
            }
        } catch (error) {
            console.error("Error fetching bonuses:", error);
        }
    }, [uid, currentUserUid]);

    useEffect(() => {
        if (isLoggedIn && currentUserUid) {
            // Check for session_id from Stripe redirect and verify it
            const query = new URLSearchParams(window.location.search);
            const sessionId = query.get("session_id");
            if (sessionId) {
                verifyCheckoutSession(sessionId).then(() => {
                    fetchStorageDetails();
                    fetchBonuses();
                });
                // Clean the URL so refresh doesn't re-verify
                window.history.replaceState({}, document.title, window.location.pathname);
            } else {
                fetchStorageDetails();
                fetchBonuses();
            }
        }
    }, [isLoggedIn, currentUserUid, fetchStorageDetails, fetchBonuses]);

    const verifyCheckoutSession = async (sessionId) => {
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${API_BASE}/verify-checkout-session/${sessionId}/${uid}/`, {
                headers: {
                    "Authorization": `${token}`,
                },
            });
            const data = await response.json();
            if (data.payment_status === "paid") {
                setUpgradeSuccess(true);
            }
        } catch (error) {
            console.error("Error verifying checkout session:", error);
        }
    };

    const handleBillingModeChange = async (newMode) => {
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${API_BASE}/users/${uid}/storage/billing-mode/`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`,
                    "auth-token": `Bearer ${token}`,
                    "requesting-user-uid": currentUserUid,
                },
                body: JSON.stringify({ billing_mode: newMode }),
            });
            if (response.ok) {
                setBillingMode(newMode);
                fetchStorageDetails();
            } else {
                const data = await response.json();
                alert(data.error || "Failed to update billing mode");
            }
        } catch (error) {
            console.error("Error updating billing mode:", error);
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    };

    const getProgressClass = (percentage) => {
        if (percentage >= 90) return "StorageManagement__progress-bar-fill--danger";
        if (percentage >= 75) return "StorageManagement__progress-bar-fill--warning";
        return "";
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="StorageManagement__loading">
                    <p>Loading storage information...</p>
                </div>
            </>
        );
    }

    const info = storageData?.storage_info;

    return (
        <>
            <Header />
            <div className="StorageManagement__title-section">
                <h1>Storage Management</h1>
            </div>
            <div className="StorageManagement__main-content">
                <button className="StorageManagement__navigation-buttons" onClick={() => navigate(`/account/${uid}`)}>
                    <i className="fas fa-arrow-left"></i> Back to Account
                </button>

                {upgradeSuccess && (
                    <div style={{
                        padding: "12px 20px",
                        marginBottom: "20px",
                        borderRadius: "8px",
                        backgroundColor: "var(--success-bg-color, #d4edda)",
                        color: "var(--success-text-color, #155724)",
                        border: "1px solid var(--success-border-color, #c3e6cb)",
                    }}>
                        <strong>Plan upgraded successfully!</strong> Your storage limit has been updated.
                    </div>
                )}

                {/* Storage Overview */}
                {info && (
                    <div className="StorageManagement__overview-card">
                        <div className="StorageManagement__usage-header">
                            <h3>Storage Usage</h3>
                            <span className="StorageManagement__plan-badge">
                                {info.plan_name} Plan
                            </span>
                        </div>

                        <div className="StorageManagement__progress-bar-container">
                            <div
                                className={`StorageManagement__progress-bar-fill ${getProgressClass(info.storage_used_percentage)}`}
                                style={{ width: `${Math.min(info.storage_used_percentage, 100)}%` }}
                            />
                        </div>

                        <div className="StorageManagement__usage-details">
                            <div>
                                <span>{formatBytes(info.storage_used_bytes)}</span> of <span>{formatBytes(info.storage_limit_bytes)}</span> used
                            </div>
                            <div>
                                <span>{formatBytes(info.storage_remaining_bytes)}</span> remaining
                            </div>
                        </div>

                        <div className="StorageManagement__stats-grid">
                            <div className="StorageManagement__stat-card">
                                <span className="StorageManagement__stat-value">{Math.round(info.storage_used_percentage)}%</span>
                                <span className="StorageManagement__stat-label">Used</span>
                            </div>
                            <div className="StorageManagement__stat-card">
                                <span className="StorageManagement__stat-value">{formatBytes(info.bonus_storage_bytes)}</span>
                                <span className="StorageManagement__stat-label">Bonus Storage</span>
                            </div>
                            <div className="StorageManagement__stat-card">
                                <span className="StorageManagement__stat-value">{info.active_bonuses_count}</span>
                                <span className="StorageManagement__stat-label">Active Bonuses</span>
                            </div>
                            <div className="StorageManagement__stat-card">
                                <span className="StorageManagement__stat-value">{info.billing_mode === "hard_cap" ? "Hard Cap" : "Pay As You Go"}</span>
                                <span className="StorageManagement__stat-label">Billing Mode</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Quick Actions */}
                <div style={{ display: "flex", gap: "12px", marginBottom: "30px", flexWrap: "wrap" }}>
                    <button className="StorageManagement__navigation-buttons" onClick={() => navigate(`/account/${uid}/plans`)}>
                        <i className="fas fa-arrow-up"></i> View Plans
                    </button>
                    <button className="StorageManagement__navigation-buttons" onClick={() => navigate(`/account/${uid}/referrals`)}>
                        <i className="fas fa-gift"></i> Referrals & Bonuses
                    </button>
                </div>

                {/* Billing Mode Section */}
                {storageData?.current_plan?.is_pay_as_you_go && (
                    <div className="StorageManagement__section">
                        <h3>Billing Mode</h3>
                        <p className="StorageManagement__billing-description">
                            Choose how your storage limit is enforced.
                        </p>
                        <div className="StorageManagement__billing-toggle">
                            <div
                                className={`StorageManagement__billing-option ${billingMode === "hard_cap" ? "StorageManagement__billing-option--active" : ""}`}
                                onClick={() => handleBillingModeChange("hard_cap")}
                            >
                                <strong>Hard Cap</strong>
                                <p style={{ margin: "4px 0 0", fontSize: "0.8rem" }}>Storage is limited. You'll be notified when you're close to your limit.</p>
                            </div>
                            <div
                                className={`StorageManagement__billing-option ${billingMode === "pay_as_you_go" ? "StorageManagement__billing-option--active" : ""}`}
                                onClick={() => handleBillingModeChange("pay_as_you_go")}
                            >
                                <strong>Pay As You Go</strong>
                                <p style={{ margin: "4px 0 0", fontSize: "0.8rem" }}>Exceed your limit freely. Extra usage is billed per GB.</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Storage Bonuses */}
                <div className="StorageManagement__section">
                    <h3>Storage Bonuses</h3>
                    {bonuses.length > 0 ? (
                        <div className="StorageManagement__bonus-list">
                            {bonuses.map((bonus) => (
                                <div key={bonus.id} className="StorageManagement__bonus-item">
                                    <div className="StorageManagement__bonus-info">
                                        <span className="StorageManagement__bonus-name">{bonus.promotion_name}</span>
                                        <span className="StorageManagement__bonus-description">
                                            {bonus.description}
                                            {bonus.expires_at && ` · Expires ${new Date(bonus.expires_at).toLocaleDateString()}`}
                                        </span>
                                    </div>
                                    <span className="StorageManagement__bonus-amount">+{bonus.bonus_mb} MB</span>
                                    <span className={`StorageManagement__bonus-status StorageManagement__bonus-status--${bonus.status === "pending_verification" ? "pending" : bonus.status}`}>
                                        {bonus.status === "pending_verification" ? "Pending" : bonus.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="StorageManagement__empty-state">
                            <p>No storage bonuses yet. Refer a friend or claim a promotion to earn bonus storage!</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default StorageManagement;
